import { __rest, __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { defaultInitQuestionOption, initQuestionsIntro, initialMessages, negativeFeedbackTemplate, feedbackVersionNumber, WIDGET_ID, FEEDBACK_REASON_CODE, positiveFeedbackTemplate } from '../../config/constants.js';
import { useState, useRef, useCallback, useEffect } from 'react';
import ChatBotContent from './components/chatBotContent.js';
import ChatBotFooter from './components/chatBotFooter.js';
import ChatBotHeader from './components/chatBotHeader.js';
import HighlightExplain from './components/highlightExplain.js';
import styles from './styles/chatbot.module.scss.js';
import { getRestURL, generateWebSocketUrlFromInitData } from '../../utils/getUrls.js';
import { ClientProvider, useClient } from '../../client/ClientProvider.js';
import { AuthCookieProvider } from './components/AuthCookieProvider.js';
import useWebSocket from 'react-use-websocket';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorContent } from './components/ErrorContent.js';

const ChatBotWidget = (props) => {
    const { allowedSelectors = [] } = props, rest = __rest(props, ["allowedSelectors"]);
    const initData = rest;
    const [expanded, setExpanded] = useState(false);
    const [messages, setMessages] = useState([]);
    const [userInput, serUserInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(true);
    const [isHistory, setIsHistory] = useState(false);
    const { client, checkServiceStatus } = useClient();
    const [sessionId, setSessionId] = useState('');
    const [isMaximized, setIsMaximized] = useState(false);
    const [isInitSessionCompleted, setIsInitSessionCompleted] = useState(false);
    const [shouldConnect, setShouldConnect] = useState(true);
    const [retryError, setRetryError] = useState(false);
    const chatbotButtonRef = useRef(null);
    const handleExpand = useCallback(() => {
        if (!expanded) {
            setExpanded(true);
        }
    }, [expanded, setExpanded]);
    const handleKeyUp = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            handleExpand();
        }
    }, [handleExpand]);
    const toggleMaximized = () => {
        setIsMaximized((prevState) => !prevState);
    };
    const { sendMessage, lastMessage } = useWebSocket(generateWebSocketUrlFromInitData(initData), {
        shouldReconnect: (_) => true,
        onReconnectStop: () => {
            setIsLoading(false);
            setRetryError(true);
        },
        reconnectAttempts: 30,
        reconnectInterval: 1000
    }, shouldConnect);
    const handleInputChange = (userInput) => {
        serUserInput(userInput);
    };
    const getInitSession = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (!initData.blueprintArn)
                return;
            const initSessionData = yield client.fetchInitSessionInfo(initData.blueprintArn);
            setIsChatHistoryLoading(false);
            setSessionId(initSessionData.sessionId);
            setIsInitSessionCompleted(true);
            let initQuestionOptions;
            if (initSessionData.zeroStateExperienceFlag) {
                if (initSessionData.initQuestions) {
                    initQuestionOptions = [
                        ...new Set([
                            ...defaultInitQuestionOption,
                            ...initSessionData.initQuestions
                        ])
                    ];
                }
                else {
                    initQuestionOptions = defaultInitQuestionOption;
                }
                const initQuestionMessage = {
                    sender: 'bot',
                    text: initQuestionsIntro,
                    type: 'options',
                    options: initQuestionOptions
                };
                // Creates a new array without mutating initialMessages
                // initialMessages is imported from your constants, mutating it affects all references to it
                // leading to unexpected behavior when the function runs twice
                const newMessages = [...initialMessages, initQuestionMessage];
                setMessages(newMessages);
            }
            else {
                setIsHistory(true);
                const chatHistory = [];
                for (const chatMessage of initSessionData.chatHistory) {
                    const message = {
                        messageId: chatMessage.messageId,
                        text: chatMessage.messageText,
                        sender: chatMessage.messageOwner === 'user' ? 'user' : 'bot',
                        type: chatMessage.messageOwner === 'user' ? 'request' : 'response',
                        feedback: chatMessage.feedback,
                        feedbackReasonCode: chatMessage.feedbackReasonCode
                    };
                    chatHistory.push(message);
                    if (message.feedbackReasonCode) {
                        chatHistory.push({
                            messageId: chatMessage.messageId,
                            text: negativeFeedbackTemplate[feedbackVersionNumber].text,
                            sender: 'bot',
                            type: 'options',
                            options: negativeFeedbackTemplate[feedbackVersionNumber].options,
                            selection: message.feedbackReasonCode
                        });
                    }
                }
                setMessages(chatHistory);
            }
        }
        catch (error) {
            setIsChatHistoryLoading(false);
            setIsInitSessionCompleted(true);
            console.error(error);
        }
    }), [client, initData.blueprintArn]);
    const sendUserMessage = useCallback((userMessage) => __awaiter(void 0, void 0, void 0, function* () {
        // Re-fetch the updated isHealthy status from api, use the resolved value to avoid race condition
        const data = yield checkServiceStatus();
        // Safely checks if data is an object and doesn't attempt to access andonCordPulled if data is undefined or null.
        // It will only set isHealthy to false if data has attribute andonCordPulled and it's value is true
        const updatedIsHealthy = (data === null || data === void 0 ? void 0 : data.andonCordPulled) ? false : true;
        if (updatedIsHealthy) {
            // Check system healthy status on every new messages
            setIsHistory(false);
            setMessages((prevMessage) => [...prevMessage, userMessage]);
            serUserInput('');
            const message = JSON.stringify(Object.assign({ sessionId: sessionId, action: 'sendMessage', message: userMessage.text, response_format: 'html' }, (initData.blueprintArn && { blueprintArn: initData.blueprintArn })));
            sendMessage(message);
            setIsLoading(true);
        }
    }), [sessionId, initData.blueprintArn, sendMessage, checkServiceStatus]);
    const handleMessage = useCallback((rawData) => {
        const data = JSON.parse(rawData);
        if (data.message !== '') {
            const botMessage = {
                text: data.message,
                sender: 'bot',
                type: 'response',
                messageId: data.message_id
            };
            setMessages((prevMessage) => [...prevMessage, botMessage]);
            setIsLoading(false);
        }
    }, []);
    useEffect(() => {
        if (lastMessage !== null) {
            handleMessage(lastMessage.data);
        }
    }, [handleMessage, lastMessage]);
    const handleSendMessage = () => {
        if (userInput.trim() !== '') {
            const userMessage = { text: userInput, sender: 'user' };
            sendUserMessage(userMessage);
        }
    };
    /**
     * @param {string} sessionId
     * @param {string} messageId
     * @param {string} feedback Feedback sends to backend
     * @param {string} feedbackReasonCode Feedback reason send to backend, backend seems doesn't have a fixed code for it for now.
     * @todo Please follow up on the token and sessionId, messageId
     */
    const submitFeedback = (messageId_1, feedback_1, ...args_1) => __awaiter(void 0, [messageId_1, feedback_1, ...args_1], void 0, function* (messageId, feedback, feedbackReasonCode = '') {
        const feedbackMessage = {
            sessionId: sessionId,
            messageId: messageId,
            feedback: feedback,
            feedbackReasonCode: feedbackReasonCode
        };
        yield client.submitFeedback(feedbackMessage);
    });
    const handleOptionButtonClick = (msg, option) => {
        if (msg.selection)
            return;
        const optionCode = FEEDBACK_REASON_CODE[option];
        if (negativeFeedbackTemplate[feedbackVersionNumber].options.indexOf(option.trim()) >= 0) {
            msg.selection = FEEDBACK_REASON_CODE[option];
            const botMessage = {
                text: negativeFeedbackTemplate[feedbackVersionNumber].response,
                sender: 'bot'
            };
            setMessages((prevMessage) => [...prevMessage, botMessage]);
            if (msg.messageId) {
                submitFeedback(msg.messageId, 'Negative', optionCode);
            }
        }
        else if (option.trim() !== '') {
            const userMessage = { text: option, sender: 'user' };
            sendUserMessage(userMessage);
        }
    };
    const explainText = useCallback((text) => {
        if (isLoading)
            return;
        if (text.trim() !== '') {
            const userMessage = {
                text: `Please explain ${text}`,
                sender: 'user'
            };
            sendUserMessage(userMessage);
        }
    }, [sendUserMessage, isLoading]);
    const expandIfClosed = useCallback(() => {
        if (!expanded)
            setExpanded(true);
    }, [expanded]);
    useEffect(() => {
        getInitSession().catch((err) => console.log(err));
        // Want to execute this block only in initial load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        document.addEventListener('openWidget', expandIfClosed);
        return () => {
            document.removeEventListener('openWidget', expandIfClosed);
        };
    }, [expandIfClosed]);
    const refresh = useCallback(() => {
        getInitSession().catch((err) => console.log(err));
        setShouldConnect(false);
        setTimeout(() => setShouldConnect(true), 10); // Put the logic "set shouldReconnect=true" to event loop in order to trigger the websocket reconnection
        setRetryError(false);
    }, [getInitSession]);
    const openChatBot = () => setExpanded(true);
    const closeChatBot = () => {
        setExpanded(false);
        requestAnimationFrame(() => {
            var _a;
            (_a = chatbotButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        });
    };
    const handleFeedbackButtonClick = (msg, feedback) => {
        msg.feedback = feedback;
        if (feedback === 'Negative') {
            setMessages((prevMessage) => [
                ...prevMessage,
                {
                    text: negativeFeedbackTemplate[feedbackVersionNumber].text,
                    sender: 'bot',
                    type: 'options',
                    options: negativeFeedbackTemplate[feedbackVersionNumber].options,
                    messageId: msg.messageId
                }
            ]);
        }
        else if (feedback === 'Positive') {
            setMessages((prevMessage) => [
                ...prevMessage,
                {
                    text: positiveFeedbackTemplate[feedbackVersionNumber].text,
                    sender: 'bot',
                    type: 'response',
                    feedback: 'disabled',
                    messageId: msg.messageId
                }
            ]);
            if (msg.messageId) {
                submitFeedback(msg.messageId, 'Positive');
            }
        }
    };
    return (jsxs("div", { className: `${styles.chatbot} ${styles['chatbot-button']} ${expanded
            ? `${styles.expanded} ${isMaximized ? styles['maximized-width'] : ''}`
            : styles['chatbot-button']}`, id: WIDGET_ID, onClick: handleExpand, "aria-label": 'Learner assistant control button', role: 'button', tabIndex: 0, onKeyUp: handleKeyUp, ref: chatbotButtonRef, "data-testid": 'chatbotOpenBtn', children: [jsx(ChatBotHeader, { expanded: expanded, isLoading: isChatHistoryLoading, openChatBot: openChatBot, closeChatBot: closeChatBot, isMaximized: isMaximized, toggleMaximized: toggleMaximized }), expanded && (jsxs(Fragment, { children: [retryError ? (jsx(ErrorContent, { onRefreshClicked: refresh })) : (jsx(ChatBotContent, { messages: messages, handleOptionButtonClick: handleOptionButtonClick, handleFeedbackButtonClick: handleFeedbackButtonClick, showLoadingDots: isLoading, displayBottom: isHistory })), jsx(ChatBotFooter, { userInput: userInput, onInputChange: handleInputChange, onEnterPress: handleSendMessage, isMaximized: isMaximized, disabled: isLoading || !isInitSessionCompleted || retryError })] })), jsx(HighlightExplain, { onExplain: explainText, expandIfClosed: expandIfClosed, allowedSelectors: allowedSelectors })] }));
};
const ChatBot = (props) => {
    const { allowedSelectors } = props, rest = __rest(props, ["allowedSelectors"]);
    const initData = rest;
    if (!initData.gandalfToken && !initData.exemptGandalfToken) {
        console.warn('No token is passed from parent.');
        return jsx(Fragment, {});
    }
    return (jsx(ErrorBoundary, { fallback: jsx(Fragment, {}), children: jsx(ClientProvider, { endpoint: getRestURL(window.location.hostname), initData: initData, children: jsx(AuthCookieProvider, { gandalfToken: initData.gandalfToken, children: jsx(ChatBotWidget, Object.assign({ allowedSelectors: allowedSelectors }, initData)) }) }) }));
};

export { ChatBot as default };
